/*
 * Сроки тарифа ПРО — один источник для главной и для /pricing.
 *
 * Раньше «год» и «навсегда» жили мелкой серой строкой под ценой месяца,
 * и их просто не видели. Теперь это крупный переключатель внутри самой
 * карточки ПРО: цена меняется прямо на месте, по умолчанию — месяц.
 *
 * benefit считается от месячной цены и пишется словами, а не процентами:
 * «выгода 3 088 ₽ за год» человеку понятнее, чем «−26%».
 */

export const PRO_MONTHLY_PRICE = 999;

export const PRO_TERMS = [
  {
    key: 'month',
    label: 'Месяц',
    price: '999 ₽',
    unit: '/мес',
    plan: 'PRO_MONTHLY',
    path: '/checkout?plan=PRO_MONTHLY',
    benefit: null,
    // Для прямой оплаты на /pricing: тип плана и признак «годовой».
    payType: 'PRO',
    payYearly: false,
    payLabel: '999 ₽/мес',
    termNote: '1 месяц',
  },
  {
    key: 'year',
    label: 'Год',
    price: '8 900 ₽',
    unit: '/год',
    plan: 'PRO_YEARLY',
    path: '/checkout?plan=PRO_YEARLY',
    // 999 × 12 = 11 988 ₽; 11 988 − 8 900 = 3 088 ₽.
    benefit: 'Вместо 11 988 ₽ помесячно — выгода 3 088 ₽ за год',
    payType: 'PRO',
    payYearly: true,
    payLabel: '8 900 ₽/год',
    termNote: '1 год',
  },
  {
    key: 'forever',
    label: 'Навсегда',
    price: '14 900 ₽',
    unit: 'разово',
    plan: 'FOUNDER',
    path: '/checkout?plan=FOUNDER',
    // 14 900 / 999 ≈ 14,9 месяца.
    benefit: 'Разовый платёж: как 15 месяцев по 999 ₽, дальше не списывается ничего',
    payType: 'FOUNDER',
    payYearly: false,
    payLabel: '14 900 ₽ навсегда',
    termNote: 'бессрочно',
  },
];

export const DEFAULT_PRO_TERM = 'month';

// ==================== ДВОЙНАЯ ВЫГОДА ====================
// Код плана и цены дублировать нельзя: право на 499 и саму цену решает
// сервер (backend/double_benefit.py), здесь только то, что показываем.
export const DOUBLE_BENEFIT_PLAN = 'PRO_MONTHLY_X2';
export const DOUBLE_BENEFIT_PRICE = 499;
export const DOUBLE_BENEFIT_PATH = `/checkout?plan=${DOUBLE_BENEFIT_PLAN}`;

// Сколько дней ПРО даёт оплаченный месяц. Значение решает сервер — разбор
// кода плана (backend/routers/yookassa.py, PLAN_ACCESS PRO_MONTHLY_X2.days);
// здесь копия только для показа «всего 30+N дней».
export const PRO_MONTH_DAYS = 30;

/** «день / дня / дней» по числу: 1 день, 2 дня, 5 дней, 21 день. */
export function dayWord(n) {
  const value = Math.abs(Number(n) || 0);
  const tens = value % 100;
  if (tens >= 11 && tens <= 14) return 'дней';
  const ones = value % 10;
  if (ones === 1) return 'день';
  if (ones >= 2 && ones <= 4) return 'дня';
  return 'дней';
}

// ==================== Каталог планов ====================
// Единый справочник для страницы оплаты: раньше у /checkout была своя копия
// цен, и после правки витрины человек видел на оплате старое число.
// Цены ПРО берутся из PRO_TERMS выше, чтобы источник остался один.
const proTerm = (key) => PRO_TERMS.find((t) => t.key === key);

export const PLAN_CATALOG = {
  // ЛАЙТ 599 ₽ снят с продажи (30.09.2026): его планов здесь нет, и
  // /checkout?plan=ASTROFEEL_* уводит на /pricing, как любой неизвестный план.
  PRO_MONTHLY: {
    name: 'ПРО',
    priceLabel: `${proTerm('month').price} / мес`,
    subscription: true,
    blurb: 'Все программы без ограничений на месяц: профессия, совместимость, предназначение, хорар без лимитов.',
  },
  PRO_YEARLY: {
    name: 'ПРО — год',
    priceLabel: `${proTerm('year').price} / год`,
    subscription: true,
    blurb: 'Все программы без ограничений, выгоднее при оплате за год.',
  },
  FOUNDER: {
    name: 'ПРО навсегда',
    priceLabel: `${proTerm('forever').price} — навсегда`,
    subscription: false,
    blurb: 'Все программы без ограничений навсегда. Разовый платёж, без автопродления.',
  },
  [DOUBLE_BENEFIT_PLAN]: {
    name: 'ПРО — первый месяц',
    priceLabel: `${DOUBLE_BENEFIT_PRICE} ₽ / мес`,
    subscription: true,
    blurb: 'Все программы без ограничений. Первый месяц по акции, дальше обычная цена.',
  },
};

// Семейство ПРО на странице оплаты: срок переключается прямо там, человек
// пришёл платить и должен видеть все три цены рядом.
export const CHECKOUT_PRO_TERMS = PRO_TERMS.map((t) => ({
  key: t.plan,
  term: t.label,
  price: t.price,
}));

export function findProTerm(key) {
  return PRO_TERMS.find((t) => t.key === key) || PRO_TERMS[0];
}
